import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Timer } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { scrollToId } from "./Nav";

const Countdown = ({ offers, o }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);
  const dates = offers.map((x) => x.promo_until).filter(Boolean).sort();
  if (dates.length === 0) return null;
  const target = new Date(`${dates[dates.length - 1]}T23:59:59`);
  const ms = target - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const dateLabel = target.toLocaleDateString(o.locale, { day: "numeric", month: "long" });
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.15 }}
      className="inline-flex items-center gap-3 border border-[#FF7900]/40 bg-[#FF7900]/10 text-[#FF7900] px-5 py-2.5 rounded-full text-sm font-semibold mb-14"
      data-testid="promo-countdown"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF7900] opacity-60" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF7900]" />
      </span>
      <Timer className="w-4 h-4" />
      {o.countdownLabel} {dateLabel} — J−{days} · {hours}h
    </motion.div>
  );
};

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export const OffersShowcase = () => {
  const { lang, t } = useLang();
  const o = t.offersSection;
  const [offers, setOffers] = useState([]);
  const [, tick] = useState(0);

  useEffect(() => {
    api.get("/offers").then((res) => setOffers(res.data.filter((x) => x.active)));
    const id = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const offerLeft = (d) => {
    if (!d) return null;
    const ms = new Date(`${d}T23:59:59`) - Date.now();
    if (ms <= 0) return null;
    return {
      days: Math.floor(ms / 86400000),
      hours: Math.floor((ms % 86400000) / 3600000),
      urgent: ms <= 48 * 3600000,
    };
  };

  if (offers.length === 0) return null;

  return (
    <section id="offers" className="py-24 md:py-32 border-t border-white/10" data-testid="offers-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.p {...fade} transition={{ duration: 0.6 }} className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FF7900] mb-6">
          {o.overline}
        </motion.p>
        <motion.h2
          {...fade}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-display text-4xl md:text-5xl font-medium tracking-tight max-w-3xl mb-4"
        >
          {o.title}
        </motion.h2>
        <motion.p {...fade} transition={{ duration: 0.7, delay: 0.1 }} className="text-white/60 max-w-2xl mb-8">
          {o.sub}
        </motion.p>
        <div>
          <Countdown offers={offers} o={{ countdownLabel: o.countdownLabel, locale: lang === "fr" ? "fr-BE" : "en-GB" }} />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {["orange", "eneco"].map((cat, ci) => (
            <motion.div
              key={cat}
              {...fade}
              transition={{ duration: 0.8, delay: 0.1 + ci * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="border border-white/10 bg-[#111111]"
              data-testid={`offers-column-${cat}`}
            >
              <div className="px-7 md:px-9 py-6 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-display text-xl md:text-2xl font-medium">{o[cat]}</h3>
                <span
                  className={`text-xs uppercase tracking-[0.2em] px-4 py-1.5 rounded-full ${
                    cat === "orange" ? "bg-[#FF7900] text-white" : "bg-white text-black"
                  }`}
                >
                  {cat === "orange" ? "Telecom" : lang === "fr" ? "Énergie" : "Energy"}
                </span>
              </div>
              <ul>
                {offers
                  .filter((x) => x.category === cat)
                  .map((of) => (
                    <li
                      key={of.id}
                      className="px-7 md:px-9 py-5 border-b border-white/5 last:border-b-0 flex items-baseline justify-between gap-6 group hover:bg-white/[0.02] transition-colors duration-300"
                      data-testid={`public-offer-${of.id}`}
                    >
                      <div className="flex-1 min-w-[55%]">
                        <p className="font-semibold text-sm md:text-base">{of.name}</p>
                        <p className="text-xs md:text-sm text-white/45 mt-1 leading-relaxed">
                          {lang === "fr" ? of.detail_fr : of.detail_en}
                        </p>
                        {(() => {
                          const left = offerLeft(of.promo_until);
                          if (!left) return null;
                          return (
                            <span
                              data-testid={`offer-countdown-${of.id}`}
                              className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
                                left.urgent
                                  ? "text-red-400 border-red-500/40 bg-red-500/10"
                                  : "text-[#FF7900] border-[#FF7900]/35 bg-[#FF7900]/10"
                              }`}
                            >
                              <Timer className="w-3 h-3" />
                              {o.endsIn} {left.days > 0 ? `${left.days}${lang === "fr" ? "j" : "d"} ` : ""}{left.hours}h
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-[#FF7900] font-display font-medium text-sm md:text-base text-right max-w-[45%] shrink-0 leading-snug">
                        {of.price}
                      </p>
                    </li>
                  ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div {...fade} transition={{ duration: 0.7, delay: 0.2 }} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <button
            onClick={() => scrollToId("contact")}
            data-testid="offers-cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-300 w-fit"
          >
            {o.cta}
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-white/35 max-w-md">{o.note}</p>
        </motion.div>
      </div>
    </section>
  );
};
